
import Vue from 'vue';
import { db, storage } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { mapActions, mapState } from 'vuex';
import RunnitImage from '../RunnitImage.vue';
import LoadingSVG from '@/assets/LoadingSVG.vue';
import {
	Avatar,
	RunnitNodeRun,
	RunnitNodeRunResult,
	RUNNIT_NODE_RUN_STATE,
	RUNNIT_NODE_RUN_RESULT_TYPE,
	AVATAR_USE_CASE,
	UPLOAD_FILE_TYPE,
	getRunnitNodeRunResultFilePath,
} from '@run-diffusion/shared';
import { ImageUploadMixin } from '@/mixins/ImageUploadMixin';
import { v4 as uuidv4 } from 'uuid';
import { uploadImageRecordForUseCase } from '@/utils/runnitUpload';

export default Vue.extend({
	name: 'HeroImageLibraryGallery',
	mixins: [
		ImageUploadMixin,
	],
	data () {
		return {
			RUNNIT_NODE_RUN_STATE,
			RUNNIT_NODE_RUN_RESULT_TYPE,
			AVATAR_USE_CASE,
			UPLOAD_FILE_TYPE,
			FINAL_NODE_RUN_STATES: [
				RUNNIT_NODE_RUN_STATE.NSFW,
				RUNNIT_NODE_RUN_STATE.ERROR,
				RUNNIT_NODE_RUN_STATE.DONE,
			],
			loading: false,
			nodeRuns: [] as RunnitNodeRun[],
			imageResults: [] as RunnitNodeRunResult[],
			creatingAvatarUuid: null as string | null,
			canLoadMore: true,
			loadingMore: false,
			lastDoc: null as any,
			limit: 40,
		};
	},
	computed: {
		...mapState([
			'user',
		]),
	},
	created () {
		this.loadNodeRuns();
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		async loadNodeRuns () {
			if (this.loading || this.loadingMore || !this.user?.id || !this.canLoadMore) return;

			try {
				// Treat the first fetch differently from pagination
				if (!this.nodeRuns.length) {
					this.loading = true;
				} else {
					this.loadingMore = true;
				}

				// index reused = runnitNodeRuns: deletedAt Ascending state Ascending userId Ascending templateOriginalData Ascending initAt Descending __name__ Descending
				let nodeRunsRef = db.collection('runnitNodeRuns')
					.where('deletedAt', '==', null)
					.where('state', 'in', this.FINAL_NODE_RUN_STATES)
					.where('userId', '==', this.user.id)
					.where('templateOriginalData', '==', null) // Filter out template clones in library
					.orderBy('initAt', 'desc')
					.limit(this.limit);

				if (this.lastDoc) {
					nodeRunsRef = nodeRunsRef.startAfter(this.lastDoc);
				}

				const snapshot = await nodeRunsRef.get();

				if (snapshot.empty) {
					this.canLoadMore = false;
				} else {
					this.canLoadMore = snapshot.docs.length === this.limit;
					if (snapshot.docs.length > 0) {
						this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
					}

					const newNodeRuns: RunnitNodeRun[] = snapshot.docs.map(doc => ({
						...doc.data(),
						get id () { return doc.id; },
					})) as RunnitNodeRun[];

					this.nodeRuns = [...this.nodeRuns, ...newNodeRuns];
					this.flattenImageResults();
				}
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading generations',
					show: true,
				});
			} finally {
				this.loading = false;
				this.loadingMore = false;
			}
		},
		onInfiniteScroll (entries) {
			if (!entries || !entries.length || !entries[0].isIntersecting) return;
			// Only paginate when user has already loaded the first page and isn't currently fetching
			if (this.nodeRuns.length && this.canLoadMore && !this.loading && !this.loadingMore) {
				this.loadNodeRuns();
			}
		},
		flattenImageResults () {
			const results: RunnitNodeRunResult[] = [];
			const seenUuids = new Set<string>();

			for (const nodeRun of this.nodeRuns) {
				if (!nodeRun.results) continue;

				for (const result of nodeRun.results) {
					if (
						result.type === RUNNIT_NODE_RUN_RESULT_TYPE.IMG &&
						result.isNsfw !== true &&
						!result.deletedAt &&
						!seenUuids.has(result.uuid)
					) {
						results.push(result);
						seenUuids.add(result.uuid);
					}
				}
			}

			this.imageResults = results;
		},
		async handleImageClick (result: RunnitNodeRunResult) {
			if (this.creatingAvatarUuid || !this.user?.id) return;

			try {
				this.creatingAvatarUuid = result.uuid;

				// Get the storage path and download the image
				const { filePath } = getRunnitNodeRunResultFilePath(result);
				const fileRef = storage.ref(filePath);
				const downloadUrl = await fileRef.getDownloadURL();

				// Fetch the image as a blob
				const response = await fetch(downloadUrl);
				const blob = await response.blob();

				// Create a File from the blob
				const fileName = result.file?.name || 'image.png';
				const file = new File([blob], fileName, { type: blob.type || 'image/png' });

				// Create avatar from the file (replicating ImageUpload.vue's handleAvatar logic)
				const avatar = await this.createAvatarFromFile(file);

				if (avatar) {
					this.$emit('avatar-created', avatar);
				}
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error creating avatar from generation',
					show: true,
				});
			} finally {
				this.creatingAvatarUuid = null;
			}
		},
		async createAvatarFromFile (file: File): Promise<Avatar | null> {
			if (!this.user?.id) return null;
			return await uploadImageRecordForUseCase<Avatar>({
				file,
				uploadType: 'AVATAR',
				useCase: AVATAR_USE_CASE.RUNNIT,
			});
		},
	},
	components: {
		RunnitImage,
		LoadingSVG,
	},
});
